/**
 * GAME 3 — VITRAIL (nonogramme, unicité garantie par solveur de lignes).
 * Difficulté durcie vs le prototype : le générateur cible une profondeur de
 * déduction (`vgDepth`, nb de balayages) croissante avec le niveau, densités
 * dédiées 15×15 et Négatif re-gradées, Motif secret étendu au 5×5, Chef-d'œuvre
 * sans ligne « gratuite », cases pré-révélées aux niveaux 0-1 (accessibilité),
 * `par` posé pour le score.
 */
import type { GameEngine, PuzzleBase } from '@ordre-cache/shared';
import { ri, shuffle } from '@ordre-cache/shared';
import { mk } from './util';

/** Profondeur `vgDepth` minimale ciblée par le générateur, par taille puis niveau. */
const VG_MIN_DEPTH: Record<number, [number, number, number]> = {
  5: [2, 3, 4],
  10: [3, 5, 7],
  15: [4, 6, 9],
  18: [4, 6, 9],
};
/** Profondeur cible par (taille, niveau) — repli sur [4,6,9] pour toute taille non listée. */
function vgMinDepth(N: number, diff: number): number {
  return (VG_MIN_DEPTH[N] ?? [4, 6, 9])[diff];
}

export interface VitrailPuzzle extends PuzzleBase {
  g: 'vitrail';
  invert: boolean;
  sol: number[][];
  rowR: number[][];
  colR: number[][];
  cur: number[][];
  lives: number | null;
  livesMax: number | null;
  unfair: boolean;
  flash?: [number, number] | null;
}

export function vgRuns(line: number[]): number[] {
  const r: number[] = [];
  let n = 0;
  for (const v of line) {
    if (v) n++;
    else if (n) {
      r.push(n);
      n = 0;
    }
  }
  if (n) r.push(n);
  return r.length ? r : [0];
}
export function vgFeas(vals: number[], runs: number[]): boolean {
  if (runs.length === 1 && runs[0] === 0) return vals.every((v) => v !== 1);
  const n = vals.length,
    k = runs.length;
  const memo = Array.from({ length: n + 2 }, () => new Array<number>(k + 1).fill(-1));
  const can = (i: number, j: number): number => {
    if (i >= n) return j >= k ? 1 : 0;
    if (memo[i][j] !== -1) return memo[i][j];
    let res = 0;
    if (vals[i] !== 1) res = can(i + 1, j);
    if (!res && j < k) {
      const L = runs[j];
      if (i + L <= n) {
        let okRun = true;
        for (let t = i; t < i + L; t++)
          if (vals[t] === 0) {
            okRun = false;
            break;
          }
        if (okRun && (i + L === n || vals[i + L] !== 1))
          res = can(i + L + (i + L < n ? 1 : 0), j + 1);
      }
    }
    memo[i][j] = res;
    return res;
  };
  return !!can(0, 0);
}
function vgDeduce(vals: number[], runs: number[]): boolean {
  let ch = false;
  for (let i = 0; i < vals.length; i++) {
    if (vals[i] !== -1) continue;
    vals[i] = 1;
    const f1 = vgFeas(vals, runs);
    vals[i] = 0;
    const f0 = vgFeas(vals, runs);
    if (f1 && !f0) {
      vals[i] = 1;
      ch = true;
    } else if (f0 && !f1) {
      vals[i] = 0;
      ch = true;
    } else vals[i] = -1;
  }
  return ch;
}
/**
 * Profondeur de déduction : nombre de balayages (lignes+colonnes) nécessaires
 * pour tout résoudre par pure logique de lignes, ou -1 si non résoluble ainsi.
 * C'est LA mesure de difficulté du générateur (plus profond = plus dur).
 */
export function vgDepth(sol: number[][], N: number, invert: boolean): number {
  const tgt = invert ? sol.map((r) => r.map((v) => 1 - v)) : sol;
  const rowR = tgt.map(vgRuns);
  const colR: number[][] = [];
  for (let c = 0; c < N; c++) {
    const col: number[] = [];
    for (let r = 0; r < N; r++) col.push(tgt[r][c]);
    colR.push(vgRuns(col));
  }
  const w = mk(N, -1);
  const full = (): boolean => {
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (w[r][c] === -1) return false;
    return true;
  };
  for (let it = 0; it < N * 4; it++) {
    let ch = false;
    for (let r = 0; r < N; r++) {
      if (!w[r].includes(-1)) continue; // ligne complète : rien à déduire
      const line = w[r].slice();
      if (vgDeduce(line, rowR[r])) {
        w[r] = line;
        ch = true;
      }
    }
    for (let c = 0; c < N; c++) {
      const line: number[] = [];
      for (let r = 0; r < N; r++) line.push(w[r][c]);
      if (!line.includes(-1)) continue;
      if (vgDeduce(line, colR[c])) {
        for (let r = 0; r < N; r++) w[r][c] = line[r];
        ch = true;
      }
    }
    if (full()) return it + 1;
    if (!ch) return -1;
  }
  return -1;
}
export function vgLineSolvable(sol: number[][], N: number, invert: boolean): boolean {
  return vgDepth(sol, N, invert) >= 0;
}
/** Lignes « gratuites » (résolues d'office) : vides ([0]) ou à placement forcé (somme+écarts = N). */
function vgFreeLines(sol: number[][], N: number, invert: boolean): number {
  const tgt = invert ? sol.map((r) => r.map((v) => 1 - v)) : sol;
  const lines: number[][] = tgt.map(vgRuns);
  for (let c = 0; c < N; c++) {
    const col: number[] = [];
    for (let r = 0; r < N; r++) col.push(tgt[r][c]);
    lines.push(vgRuns(col));
  }
  let n = 0;
  for (const runs of lines)
    if (
      (runs.length === 1 && runs[0] === 0) ||
      runs.reduce((a, b) => a + b, 0) + runs.length - 1 === N
    )
      n++;
  return n;
}
const VG_PATTERNS: Array<{ name: string; grid: string[] }> = [
  {
    name: 'Cœur',
    grid: [
      '.##....##.',
      '####..####',
      '##########',
      '.########.',
      '.########.',
      '..######..',
      '..######..',
      '...####...',
      '....##....',
      '....##....',
    ],
  },
  {
    name: 'Étoile',
    grid: [
      '....##....',
      '....##....',
      '...####...',
      '##########',
      '.########.',
      '..######..',
      '..######..',
      '.###..###.',
      '.##....##.',
      '##......##',
    ],
  },
  {
    name: 'Papillon',
    grid: [
      '.##....##.',
      '####..####',
      '.########.',
      '.########.',
      '..#.##.#..',
      '....##....',
      '.########.',
      '.########.',
      '..######..',
      '....##....',
    ],
  },
  {
    name: 'Maison',
    grid: [
      '...####...',
      '..######..',
      '.########.',
      '##########',
      '..######..',
      '..#.##.#..',
      '..#.##.#..',
      '..######..',
      '..##..##..',
      '..##..##..',
    ],
  },
  {
    name: 'Poisson',
    grid: [
      '....##....',
      '...####...',
      '..######.#',
      '.#.#######',
      '##########',
      '.#########',
      '..######.#',
      '...####...',
      '....##....',
      '....##....',
    ],
  },
  {
    name: 'Fleur',
    grid: [
      '...####...',
      '..######..',
      '.###..###.',
      '.###..###.',
      '..######..',
      '...####...',
      '....##....',
      '.##.##.##.',
      '..######..',
      '....##....',
    ],
  },
  {
    name: 'Sablier',
    grid: [
      '##########',
      '.##....##.',
      '..#.##.#..',
      '...####...',
      '....##....',
      '....##....',
      '...####...',
      '..######..',
      '.########.',
      '##########',
    ],
  },
  {
    name: 'Note',
    grid: [
      '.########.',
      '.########.',
      '.##....##.',
      '.##....##.',
      '.##....##.',
      '.##....##.',
      '.##....##.',
      '###...###.',
      '###...###.',
      '###...###.',
    ],
  },
];

/** Mini-bibliothèque de motifs 5×5 pour « Motif secret » en petite grille. */
const VG_PATTERNS5: Array<{ name: string; grid: string[] }> = [
  { name: 'Cœur', grid: ['.#.#.', '#####', '#####', '.###.', '..#..'] },
  { name: 'Croix', grid: ['..#..', '..#..', '#####', '..#..', '..#..'] },
  { name: 'Flèche', grid: ['..#..', '.###.', '#####', '..#..', '..#..'] },
  { name: 'Bateau', grid: ['..#..', '.##..', '###..', '#####', '.###.'] },
];

function lineOk(p: VitrailPuzzle, idx: number, isRow: boolean): boolean {
  const N = p.N,
    line: number[] = [];
  for (let k = 0; k < N; k++)
    line.push(isRow ? (p.cur[idx][k] === 1 ? 1 : 0) : p.cur[k][idx] === 1 ? 1 : 0);
  const runs = p.invert ? vgRuns(line.map((v) => 1 - v)) : vgRuns(line);
  const ref = isRow ? p.rowR[idx] : p.colR[idx];
  return runs.length === ref.length && runs.every((v, i) => v === ref[i]);
}
function vgWon(p: VitrailPuzzle): boolean {
  for (let r = 0; r < p.N; r++) if (!lineOk(p, r, true)) return false;
  for (let c = 0; c < p.N; c++) if (!lineOk(p, c, false)) return false;
  return true;
}

export const VITRAIL: GameEngine<VitrailPuzzle> = {
  id: 'vitrail',
  name: 'Vitrail',
  icon: '🖼️',
  accent: '#ffd166',
  tag: "Déduis l'image cachée, case par case.",
  how: "Les <b>nombres</b> donnent les blocs de cases pleines de chaque ligne et colonne (dans l'ordre, séparés d'au moins une case vide). <b>Touche</b> : remplir → marquer ✕ → effacer. <b>Défis à vies</b> : ✕ d'abord (gratuit), remplir ensuite. Chaque grille a UNE seule solution, trouvable par pure logique.",
  sizes: [5, 10, 15, 18],
  defis: [
    {
      n: 'Premiers éclats',
      ic: '✨',
      d: 'Les indices comptent les cases pleines : à toi de déduire.',
    },
    { n: 'Motif secret', ic: '🖼️', d: 'Une image inconnue dort dans la grille. Réveille-la.' },
    { n: 'Trois vies', ic: '❤️', d: 'Trois erreurs permises, pas une de plus.' },
    { n: 'Négatif', ic: '🔁', d: 'Renversant : ici, les indices décrivent les cases VIDES.' },
    { n: 'Dentelle', ic: '🕸️', d: 'Indices clairsemés, déductions en profondeur.' },
    {
      n: "Chef-d'œuvre",
      ic: '🏆',
      d: 'Une vie, aucune ligne offerte : le vitrail des grands maîtres.',
    },
  ],
  gen(di, N, diff, rng) {
    const invert = di === 3;
    const minDepth = vgMinDepth(N, diff);
    let dens = N >= 15 ? [0.55, 0.51, 0.48][diff] : [0.55, 0.5, 0.44][diff];
    if (di === 4 || di === 5) dens = N >= 15 ? [0.5, 0.47, 0.45][diff] : [0.46, 0.4, 0.35][diff];
    // Négatif : gradient retourné — la cible inversée s'appauvrit quand le niveau monte
    if (di === 3) dens = [0.45, 0.5, 0.56][diff];
    let sol: number[][] | null = null;
    if (di === 1 && N <= 10) {
      // Motif secret 5×5/10×10 : motif de bibliothèque retouché par flips jusqu'à
      // résolubilité ; aux niveaux 1-2 on exige en plus une profondeur minimale,
      // à défaut le meilleur candidat résoluble rencontré.
      const lib = N === 5 ? VG_PATTERNS5 : VG_PATTERNS;
      const need = Math.max(1, (N === 5 ? [0, 3, 4] : [0, 4, 6])[diff]);
      const pat = lib[ri(rng, lib.length)];
      let cand = pat.grid.map((s) => [...s].map((ch) => (ch === '#' ? 1 : 0)));
      let best: number[][] | null = null,
        bestD = 0;
      for (let fix = 0; fix < 50; fix++) {
        const d = vgDepth(cand, N, invert);
        if (d >= need) {
          sol = cand;
          break;
        }
        if (d > bestD) {
          bestD = d;
          best = cand;
        }
        cand = cand.map((r) => r.slice());
        cand[ri(rng, N)][ri(rng, N)] ^= 1;
      }
      if (!sol) sol = best; // aucun motif résoluble → repli grille aléatoire ci-dessous
    }
    let unfair = false;
    if (!sol) {
      const tries = N === 5 ? 60 : N === 10 ? 80 : 120;
      // Chef-d'œuvre : profondeur maximale exigée quel que soit le niveau + zéro
      // ligne « gratuite » ; sinon on vise minDepth. Repli : meilleur candidat vu.
      const need = di === 5 ? vgMinDepth(N, 2) : minDepth;
      let best: number[][] | null = null,
        bestScore = -Infinity;
      for (let t = 0; t < tries; t++) {
        const d2 = t < tries * 0.7 ? dens : Math.min(0.55, dens + 0.06);
        const cand = mk(N, 0).map((row) => row.map(() => (rng() < d2 ? 1 : 0)));
        const d = vgDepth(cand, N, invert);
        if (d < 0) continue;
        const free = di === 5 ? vgFreeLines(cand, N, invert) : 0;
        if (d >= need && free === 0) {
          sol = cand;
          break;
        }
        const score = d - free * 1000; // d'abord zéro gratuite, ensuite le plus profond
        if (score > bestScore) {
          bestScore = score;
          best = cand;
        }
      }
      if (!sol) sol = best;
      if (!sol) {
        sol = mk(N, 0).map((row) => row.map(() => (rng() < dens ? 1 : 0)));
        unfair = true;
      } // secours : pénalité de vies désactivée
    }
    const tgt = invert ? sol.map((r) => r.map((v) => 1 - v)) : sol;
    const rowR = tgt.map(vgRuns);
    const colR: number[][] = [];
    for (let c = 0; c < N; c++) {
      const col: number[] = [];
      for (let r = 0; r < N; r++) col.push(tgt[r][c]);
      colR.push(vgRuns(col));
    }
    const lives = di === 2 ? 3 : di === 5 ? 1 : null;
    // Pré-révélées (accessibilité) : quelques cases justes déjà posées aux niveaux
    // 0-1, comme remplies par un coéquipier (défis à vies : cur=1 direct, sans coût).
    const kBase = Math.round(N * N * 0.06);
    const k = diff === 0 ? kBase : diff === 1 ? Math.round(kBase / 2) : 0;
    const cur = mk(N, 0);
    const filled: Array<[number, number]> = [];
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (sol[r][c] === 1) filled.push([r, c]);
    const reveal = shuffle(rng, filled.slice()).slice(0, Math.min(k, filled.length));
    for (const [r, c] of reveal) cur[r][c] = 1;
    const p: VitrailPuzzle = {
      g: 'vitrail',
      pid: ri(rng, 1e9),
      N,
      di,
      invert,
      sol,
      rowR,
      colR,
      cur,
      lives,
      livesMax: lives,
      unfair,
    };
    // jamais livré gagné : retirer des pré-révélées tant que la grille est complète
    for (let i = reveal.length - 1; i >= 0 && vgWon(p); i--) cur[reveal[i][0]][reveal[i][1]] = 0;
    let revealed = 0;
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (cur[r][c] === 1) revealed++;
    p.par = (filled.length - revealed) * (lives != null ? 2 : 1);
    return p;
  },
  tap(p, r, c) {
    if (p.lives === 0) return false;
    const v = p.cur[r][c];
    if (p.lives != null && !p.unfair) {
      // défis à vies : ✕ d'abord (gratuit), remplir ensuite (validé) — marquer ne coûte jamais
      if (v === 0) p.cur[r][c] = 2;
      else if (v === 2) {
        if (p.sol[r][c] !== 1) {
          p.lives--;
          p.flash = [r, c];
        } // reste ✕ (c'était juste !)
        else p.cur[r][c] = 1;
      } else p.cur[r][c] = 0;
      return true;
    }
    if (v === 0) p.cur[r][c] = 1;
    else if (v === 1) p.cur[r][c] = 2;
    else p.cur[r][c] = 0;
    return true;
  },
  status(p) {
    const N = p.N;
    let done = 0;
    for (let r = 0; r < N; r++) if (lineOk(p, r, true)) done++;
    for (let c = 0; c < N; c++) if (lineOk(p, c, false)) done++;
    if (p.lives === 0) return { ok: false, txt: '💔 <b>Plus de vies…</b> 🔀 Nouveau vitrail' };
    if (done === 2 * N) return { ok: true, txt: "🎉 <b>Le vitrail s'illumine !</b>" };
    // Oracle 5×5 : pas de compteur chiffré — trop révélateur sur petite grille
    let txt =
      N === 5
        ? done >= N
          ? '🧩 <b>ça prend forme…</b>'
          : '🧩 déduis avec les indices'
        : `🧩 lignes justes <b>${done}/${2 * N}</b>`;
    if (p.lives != null) txt += ` · ${'❤️'.repeat(p.lives)}${'🖤'.repeat(p.livesMax! - p.lives)}`;
    if (p.invert) txt += ' · <b>indices = cases vides !</b>';
    return { ok: false, txt };
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N;
    const mR = Math.max(...p.rowR.map((a) => a.length)),
      mC = Math.max(...p.colR.map((a) => a.length));
    const padL = Math.max(1.6, mR * 0.62),
      padT = Math.max(1.6, mC * 0.62);
    const csF = Math.min(S / (N + padL), S / (N + padT));
    const cw = csF,
      ox = padL * cw,
      oy = padT * cw;
    ctx.fillStyle = '#f6efdd';
    ctx.fillRect(0, 0, S, S);
    const st = this.status(p);
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++) {
        const x = ox + c * cw,
          y = oy + r * cw,
          v = p.cur[r][c];
        if (v === 1) {
          const hue = (r * 47 + c * 29) % 360;
          ctx.fillStyle = st.ok ? `hsl(${hue},72%,55%)` : `hsl(${hue},38%,42%)`;
          ctx.fillRect(x + 0.5, y + 0.5, cw - 1, cw - 1);
          if (st.ok) {
            ctx.strokeStyle = 'rgba(40,25,10,.85)';
            ctx.lineWidth = Math.max(1, cw * 0.09);
            ctx.strokeRect(x + 1, y + 1, cw - 2, cw - 2);
          }
        } else if (v === 2) {
          ctx.fillStyle = '#b9a98a';
          ctx.font = `${cw * 0.5}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('✕', x + cw / 2, y + cw / 2 + 1);
        }
      }
    ctx.strokeStyle = 'rgba(90,70,50,.25)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= N; i++) {
      ctx.beginPath();
      ctx.moveTo(ox + i * cw, oy);
      ctx.lineTo(ox + i * cw, oy + N * cw);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(ox, oy + i * cw);
      ctx.lineTo(ox + N * cw, oy + i * cw);
      ctx.stroke();
    }
    if (N > 5) {
      ctx.strokeStyle = 'rgba(90,70,50,.55)';
      ctx.lineWidth = 1.5;
      for (let i = 0; i <= N; i += 5) {
        ctx.beginPath();
        ctx.moveTo(ox + i * cw, oy);
        ctx.lineTo(ox + i * cw, oy + N * cw);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(ox, oy + i * cw);
        ctx.lineTo(ox + N * cw, oy + i * cw);
        ctx.stroke();
      }
    }
    ctx.font = `bold ${Math.max(8, cw * 0.42)}px sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let r = 0; r < N; r++) {
      const ok = lineOk(p, r, true);
      ctx.fillStyle = ok ? '#5aa87a' : '#4a3b28';
      const nums = p.rowR[r].join(' ');
      ctx.fillText(nums, ox - 4, oy + (r + 0.5) * cw);
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    for (let c = 0; c < N; c++) {
      const ok = lineOk(p, c, false);
      ctx.fillStyle = ok ? '#5aa87a' : '#4a3b28';
      const arr = p.colR[c];
      for (let k = 0; k < arr.length; k++) {
        ctx.fillText(
          String(arr[k]),
          ox + (c + 0.5) * cw,
          oy - 3 - (arr.length - 1 - k) * cw * 0.55,
        );
      }
    }
    if (p.flash) {
      const [r, c] = p.flash;
      ctx.fillStyle = 'rgba(255,84,112,.5)';
      ctx.fillRect(ox + c * cw, oy + r * cw, cw, cw);
      p.flash = null;
    }
  },
  solveIt(p) {
    p.cur = p.sol.map((r) => r.map((v) => (v ? 1 : 0)));
  },
};
